import {
  Context,
  EventTuple,
  ExtraContext,
} from "@utilitywarehouse/tracking-core";
import { useTracking, useTrackOnce } from "@utilitywarehouse/tracking-react";
import { SnowplowExtraContext } from "@utilitywarehouse/tracking-adapter-snowplow-web";

type EntityPayload<T> = ExtraContext<T> | SnowplowExtraContext<T>;

interface Options extends Record<string, any> {
  contexts?: Array<ExtraContext | SnowplowExtraContext>;
}

// William vulnerability journey starts
// example usage: tracker.track(trackVulnerabilityJourneyStarted({prop: 'value'}, {option1: 'value'}));
export function vulnerabilityJourneyStartedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "vulnerability_journey_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/vulnerability_journey_started/jsonschema/1-0-0",
      },
    },
  ];
}

export function useTrackVulnerabilityJourneyStarted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(vulnerabilityJourneyStartedEvent(context, options));
}

export function useTrackVulnerabilityJourneyStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(vulnerabilityJourneyStartedEvent(context, options));
}

export interface ServiceActionsListDisplayedEventContext {
  // The product that was clicked
  product_type: string;
}

// The advisor clicks on a product and displays the "service actions" list
// example usage: tracker.track(trackServiceActionsListDisplayed({prop: 'value'}, {option1: 'value'}));
export function serviceActionsListDisplayedEvent(
  context: ServiceActionsListDisplayedEventContext,
  options?: Options,
): EventTuple {
  return [
    "service_actions_list_displayed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/service_actions_list_displayed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackServiceActionsListDisplayed() {
  const { track } = useTracking();

  return (
    context: ServiceActionsListDisplayedEventContext,
    options?: Options,
  ) => track(serviceActionsListDisplayedEvent(context, options));
}

export function useTrackServiceActionsListDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: ServiceActionsListDisplayedEventContext,
    options?: Options,
  ) => trackOnce(serviceActionsListDisplayedEvent(context, options));
}

export interface VulnerabilityJourneyFinishedEventContext {
  // Whether the user is eligible for psr or not
  eligible_for_psr?: boolean;
  // Whether the user opted in for psr or not
  opted_in_for_psr?: boolean;
  // whether or not they selected to have a quarterly meter read
  quarterly_meter_read_selected?: boolean;
  // whether or not they selected to have their bill in audio
  bill_in_audio_selected?: boolean;
  // whether or not they selected to have their bill in large print
  bill_in_large_print_selected?: boolean;
  // whether or not they selected to have their bill in large braille
  bill_in_braille_selected?: boolean;
  // whether or not they selected to have a knob turner sent
  send_knob_turner_selected?: boolean;
  // whether or not they selected to set a pass phrase for when meter readers, staff, and representatives need to visit their property
  pass_phrase_selected?: boolean;
  // whether or not they selected that they required no services
  no_services_required?: boolean;
}

// William vulnerability journey has finished
// example usage: tracker.track(trackVulnerabilityJourneyFinished({prop: 'value'}, {option1: 'value'}));
export function vulnerabilityJourneyFinishedEvent(
  context: VulnerabilityJourneyFinishedEventContext,
  options?: Options,
): EventTuple {
  return [
    "vulnerability_journey_finished",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/vulnerability_journey_finished/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackVulnerabilityJourneyFinished() {
  const { track } = useTracking();

  return (
    context: VulnerabilityJourneyFinishedEventContext,
    options?: Options,
  ) => track(vulnerabilityJourneyFinishedEvent(context, options));
}

export function useTrackVulnerabilityJourneyFinishedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: VulnerabilityJourneyFinishedEventContext,
    options?: Options,
  ) => trackOnce(vulnerabilityJourneyFinishedEvent(context, options));
}

export interface ComplaintBannerClickedEventContext {
  // The detail of the complaint banner
  detail?: string;
  // The date when complaint is clicked
  date?: string;
}

// The advisor clicks a complaint banner
// example usage: tracker.track(trackComplaintBannerClicked({prop: 'value'}, {option1: 'value'}));
export function complaintBannerClickedEvent(
  context: ComplaintBannerClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "complaint_banner_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/complaint_banner_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackComplaintBannerClicked() {
  const { track } = useTracking();

  return (context: ComplaintBannerClickedEventContext, options?: Options) =>
    track(complaintBannerClickedEvent(context, options));
}

export function useTrackComplaintBannerClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: ComplaintBannerClickedEventContext, options?: Options) =>
    trackOnce(complaintBannerClickedEvent(context, options));
}

export interface PaymentAmountSelectedEventContext {
  // The payment amount option, e.g. "OVERDUE" | "DUE" | "TOTAL" | "OTHER"
  amount_option_selected: string;
  // The payment amount
  amount: number;
}

// The advisor selects the payment amount
// example usage: tracker.track(trackPaymentAmountSelected({prop: 'value'}, {option1: 'value'}));
export function paymentAmountSelectedEvent(
  context: PaymentAmountSelectedEventContext,
  options?: Options,
): EventTuple {
  return [
    "payment_amount_selected",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_amount_selected/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentAmountSelected() {
  const { track } = useTracking();

  return (context: PaymentAmountSelectedEventContext, options?: Options) =>
    track(paymentAmountSelectedEvent(context, options));
}

export function useTrackPaymentAmountSelectedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PaymentAmountSelectedEventContext, options?: Options) =>
    trackOnce(paymentAmountSelectedEvent(context, options));
}

export interface ServiceActionOpenedEventContext {
  // The service action product
  product_type: string;
  // The service action name
  service_action_name: string;
}

// The advisor opens a product's "service action"
// example usage: tracker.track(trackServiceActionOpened({prop: 'value'}, {option1: 'value'}));
export function serviceActionOpenedEvent(
  context: ServiceActionOpenedEventContext,
  options?: Options,
): EventTuple {
  return [
    "service_action_opened",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/service_action_opened/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackServiceActionOpened() {
  const { track } = useTracking();

  return (context: ServiceActionOpenedEventContext, options?: Options) =>
    track(serviceActionOpenedEvent(context, options));
}

export function useTrackServiceActionOpenedOnce() {
  const trackOnce = useTrackOnce();

  return (context: ServiceActionOpenedEventContext, options?: Options) =>
    trackOnce(serviceActionOpenedEvent(context, options));
}

// The advisor clicks on the "account number copy" button
// example usage: tracker.track(trackAccountNumberCopyButtonClicked({prop: 'value'}, {option1: 'value'}));
export function accountNumberCopyButtonClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "account_number_copy_button_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/account_number_copy_button_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAccountNumberCopyButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(accountNumberCopyButtonClickedEvent(context, options));
}

export function useTrackAccountNumberCopyButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(accountNumberCopyButtonClickedEvent(context, options));
}

export interface IdvFieldsEntity {
  // The account number verified by IDV
  verified_account_number: string;
  // The start state of IDV, e.g. "NOT_VERIFIED" | "IVR_VERIFIED" | "VERIFIED"
  idv_start_state?: string;
  // The end state of IDV, e.g. "NOT_VERIFIED" | "IVR_VERIFIED" | "VERIFIED"
  idv_end_state?: string;
}

// The details of the IDV
export function idvFieldsEntity(
  context: IdvFieldsEntity,
  options?: Options,
): EntityPayload<IdvFieldsEntity> {
  return {
    ...options,
    name: "idv_fields",
    data: context,
    snowplow: {
      schema: "iglu:uk.co.uw.customer-support/idv_fields/jsonschema/1-0-1",
    },
  };
}

// The advisor begins the "payment amount selection" journey
// example usage: tracker.track(trackPaymentAmountStarted({prop: 'value'}, {option1: 'value'}));
export function paymentAmountStartedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "payment_amount_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_amount_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentAmountStarted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(paymentAmountStartedEvent(context, options));
}

export function useTrackPaymentAmountStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(paymentAmountStartedEvent(context, options));
}

export interface VulnerabilityJourneyOpenedEventContext {
  // The title of the current step in the vulnerability journey
  title?: string;
  // The text to display current step of total steps (i.e. 2 of 10)
  step?: string;
}

// William vulnerability journey has opened
// example usage: tracker.track(trackVulnerabilityJourneyOpened({prop: 'value'}, {option1: 'value'}));
export function vulnerabilityJourneyOpenedEvent(
  context: VulnerabilityJourneyOpenedEventContext,
  options?: Options,
): EventTuple {
  return [
    "vulnerability_journey_opened",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/vulnerability_journey_opened/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackVulnerabilityJourneyOpened() {
  const { track } = useTracking();

  return (context: VulnerabilityJourneyOpenedEventContext, options?: Options) =>
    track(vulnerabilityJourneyOpenedEvent(context, options));
}

export function useTrackVulnerabilityJourneyOpenedOnce() {
  const trackOnce = useTrackOnce();

  return (context: VulnerabilityJourneyOpenedEventContext, options?: Options) =>
    trackOnce(vulnerabilityJourneyOpenedEvent(context, options));
}

export interface WilliamAppDetailsEntity {
  // The version of the app that the advisor is currently on
  app_version: string;
}

// General details of the William app
export function williamAppDetailsEntity(
  context: WilliamAppDetailsEntity,
  options?: Options,
): EntityPayload<WilliamAppDetailsEntity> {
  return {
    ...options,
    name: "william_app_details",
    data: context,
    snowplow: {
      schema:
        "iglu:uk.co.uw.customer-support/william_app_details/jsonschema/1-0-1",
    },
  };
}

export interface ComplaintActionsExpandedEventContext {
  // The id of the complaint audit
  id: string;
  // The status associated to complaint audit
  status?: string;
  // The department associated to complaint
  department: string;
  // When complaint was created
  created_at?: string;
  // When complaint was closed
  closed_at?: string;
  // The owner of the complaint
  owner?: string;
  // The cause of the complaint
  cause?: string;
  // The service associated to complaint
  service?: string;
  // Whether complaint is escalated or not
  escalated?: boolean;
  // The creator of the complaint
  created_by?: string;
  // The logic id for the complaint
  logic_id?: string;
  // The type associated to action
  type_of_action?: string;
}

// The advisor expands the complaint actions view
// example usage: tracker.track(trackComplaintActionsExpanded({prop: 'value'}, {option1: 'value'}));
export function complaintActionsExpandedEvent(
  context: ComplaintActionsExpandedEventContext,
  options?: Options,
): EventTuple {
  return [
    "complaint_actions_expanded",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/complaint_actions_expanded/jsonschema/1-0-2",
      },
    },
  ];
}

export function useTrackComplaintActionsExpanded() {
  const { track } = useTracking();

  return (context: ComplaintActionsExpandedEventContext, options?: Options) =>
    track(complaintActionsExpandedEvent(context, options));
}

export function useTrackComplaintActionsExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: ComplaintActionsExpandedEventContext, options?: Options) =>
    trackOnce(complaintActionsExpandedEvent(context, options));
}

export interface ComplaintClickedEventContext {
  // The id of the complaint
  id: string;
  // The status associated to complaint
  status?: string;
  // The department associated to complaint
  department: string;
  // When complaint was created
  created_at?: string;
  // When complaint was closed
  closed_at?: string;
  // The owner of the complaint
  owner?: string;
  // The cause of the complaint
  cause?: string;
  // The service associated to complaint
  service?: string;
  // Whether complaint is escalated or not
  escalated?: boolean;
  // The creator of the complaint
  created_by?: string;
  // The logic id for the complaint
  logic_id?: string;
}

// The advisor clicks a complaint
// example usage: tracker.track(trackComplaintClicked({prop: 'value'}, {option1: 'value'}));
export function complaintClickedEvent(
  context: ComplaintClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "complaint_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/complaint_clicked/jsonschema/1-0-2",
      },
    },
  ];
}

export function useTrackComplaintClicked() {
  const { track } = useTracking();

  return (context: ComplaintClickedEventContext, options?: Options) =>
    track(complaintClickedEvent(context, options));
}

export function useTrackComplaintClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: ComplaintClickedEventContext, options?: Options) =>
    trackOnce(complaintClickedEvent(context, options));
}

export interface ComplaintActionClickedEventContext {
  // The id of the complaint action
  id: string;
  // The type of action
  type?: string;
  // The person who the action is escalated to
  escalated_to?: string;
  // The creator of the action
  created_by?: string;
  // The status of the complaint action
  status?: string;
  // When action was created
  created_at?: string;
  // When action was completed
  completed_at?: string;
}

// The advisor clicks on a complaint's specific action
// example usage: tracker.track(trackComplaintActionClicked({prop: 'value'}, {option1: 'value'}));
export function complaintActionClickedEvent(
  context: ComplaintActionClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "complaint_action_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/complaint_action_clicked/jsonschema/1-0-2",
      },
    },
  ];
}

export function useTrackComplaintActionClicked() {
  const { track } = useTracking();

  return (context: ComplaintActionClickedEventContext, options?: Options) =>
    track(complaintActionClickedEvent(context, options));
}

export function useTrackComplaintActionClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: ComplaintActionClickedEventContext, options?: Options) =>
    trackOnce(complaintActionClickedEvent(context, options));
}

// The advisor clicks the scroll-to-top button
// example usage: tracker.track(trackNoteBackToTopClicked({prop: 'value'}, {option1: 'value'}));
export function noteBackToTopClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "note_back_to_top_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/note_back_to_top_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackNoteBackToTopClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(noteBackToTopClickedEvent(context, options));
}

export function useTrackNoteBackToTopClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(noteBackToTopClickedEvent(context, options));
}

// The advisor starts note entry
// example usage: tracker.track(trackNoteEntryStarted({prop: 'value'}, {option1: 'value'}));
export function noteEntryStartedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "note_entry_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/note_entry_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackNoteEntryStarted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(noteEntryStartedEvent(context, options));
}

export function useTrackNoteEntryStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(noteEntryStartedEvent(context, options));
}

// The advisor submits their note
// example usage: tracker.track(trackNoteEntrySubmitted({prop: 'value'}, {option1: 'value'}));
export function noteEntrySubmittedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "note_entry_submitted",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/note_entry_submitted/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackNoteEntrySubmitted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(noteEntrySubmittedEvent(context, options));
}

export function useTrackNoteEntrySubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(noteEntrySubmittedEvent(context, options));
}

// The advisor clicks the 'generate AI summary' button
// example usage: tracker.track(trackGenerateAiSummaryButtonClicked({prop: 'value'}, {option1: 'value'}));
export function generateAiSummaryButtonClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "generate_ai_summary_button_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/generate_ai_summary_button_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackGenerateAiSummaryButtonClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(generateAiSummaryButtonClickedEvent(context, options));
}

export function useTrackGenerateAiSummaryButtonClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(generateAiSummaryButtonClickedEvent(context, options));
}

export interface ComplaintAuditListExpandedEventContext {
  // The id of the complaint audit
  id: string;
  // The status of the complaint audit
  status?: string;
  // The department associated to complaint
  department: string;
  // When complaint was created
  created_at?: string;
  // When complaint was closed
  closed_at?: string;
  // The owner of the complaint
  owner?: string;
  // The cause of the complaint
  cause?: string;
  // The service associated to complaint
  service?: string;
  // Whether complaint is escalated or not
  escalated?: boolean;
  // The creator of the complaint
  created_by?: string;
  // The logic id for the complaint
  logic_id?: string;
  // The type associated to action
  type_of_action?: string;
}

// The advisor expands the complaint audit list
// example usage: tracker.track(trackComplaintAuditListExpanded({prop: 'value'}, {option1: 'value'}));
export function complaintAuditListExpandedEvent(
  context: ComplaintAuditListExpandedEventContext,
  options?: Options,
): EventTuple {
  return [
    "complaint_audit_list_expanded",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/complaint_audit_list_expanded/jsonschema/1-0-2",
      },
    },
  ];
}

export function useTrackComplaintAuditListExpanded() {
  const { track } = useTracking();

  return (context: ComplaintAuditListExpandedEventContext, options?: Options) =>
    track(complaintAuditListExpandedEvent(context, options));
}

export function useTrackComplaintAuditListExpandedOnce() {
  const trackOnce = useTrackOnce();

  return (context: ComplaintAuditListExpandedEventContext, options?: Options) =>
    trackOnce(complaintAuditListExpandedEvent(context, options));
}

export interface CommunicationClickedEventContext {
  // The id of the communication
  id: string;
  // The status associated to communication
  title?: string;
  // The code associated to communication
  code?: string;
  // Whether the communication is sensitive or not
  sensitive?: boolean;
  // When communication was sent
  sent_at?: string;
  // The type of the communication
  type?: string;
}

// The advisor clicks a communication
// example usage: tracker.track(trackCommunicationClicked({prop: 'value'}, {option1: 'value'}));
export function communicationClickedEvent(
  context: CommunicationClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "communication_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/communication_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCommunicationClicked() {
  const { track } = useTracking();

  return (context: CommunicationClickedEventContext, options?: Options) =>
    track(communicationClickedEvent(context, options));
}

export function useTrackCommunicationClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: CommunicationClickedEventContext, options?: Options) =>
    trackOnce(communicationClickedEvent(context, options));
}

// The advisor clicks a "generate vulnerability assessment" link
// example usage: tracker.track(trackVulnerabilityAssessmentGenerateLinkClicked({prop: 'value'}, {option1: 'value'}));
export function vulnerabilityAssessmentGenerateLinkClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "vulnerability_assessment_generate_link_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/vulnerability_assessment_generate_link_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackVulnerabilityAssessmentGenerateLinkClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(vulnerabilityAssessmentGenerateLinkClickedEvent(context, options));
}

export function useTrackVulnerabilityAssessmentGenerateLinkClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(
      vulnerabilityAssessmentGenerateLinkClickedEvent(context, options),
    );
}

export interface EditBillTypeChangedEventContext {
  // The current bill type
  current_bill_type?: string;
  // The selected bill type
  selected_bill_type?: string;
  // The timestamp of the change in selected bill type
  date_time?: string;
}

// The advisor changes the selected bill type
// example usage: tracker.track(trackEditBillTypeChanged({prop: 'value'}, {option1: 'value'}));
export function editBillTypeChangedEvent(
  context: EditBillTypeChangedEventContext,
  options?: Options,
): EventTuple {
  return [
    "edit_bill_type_changed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/edit_bill_type_changed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackEditBillTypeChanged() {
  const { track } = useTracking();

  return (context: EditBillTypeChangedEventContext, options?: Options) =>
    track(editBillTypeChangedEvent(context, options));
}

export function useTrackEditBillTypeChangedOnce() {
  const trackOnce = useTrackOnce();

  return (context: EditBillTypeChangedEventContext, options?: Options) =>
    trackOnce(editBillTypeChangedEvent(context, options));
}

export interface AccountOpenedEventContext {
  // True when the vulnerability tab is availabile, otherwise false
  vulnerability_tab_available: boolean;
}

// The advisor opens an account
// example usage: tracker.track(trackAccountOpened({prop: 'value'}, {option1: 'value'}));
export function accountOpenedEvent(
  context: AccountOpenedEventContext,
  options?: Options,
): EventTuple {
  return [
    "account_opened",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/account_opened/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAccountOpened() {
  const { track } = useTracking();

  return (context: AccountOpenedEventContext, options?: Options) =>
    track(accountOpenedEvent(context, options));
}

export function useTrackAccountOpenedOnce() {
  const trackOnce = useTrackOnce();

  return (context: AccountOpenedEventContext, options?: Options) =>
    trackOnce(accountOpenedEvent(context, options));
}

// The advisor opens an account in Bill
// example usage: tracker.track(trackOpenInBillClicked({prop: 'value'}, {option1: 'value'}));
export function openInBillClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "open_in_bill_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/open_in_bill_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackOpenInBillClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(openInBillClickedEvent(context, options));
}

export function useTrackOpenInBillClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(openInBillClickedEvent(context, options));
}

export interface ServiceActionClickedEventContext {
  // The action name, e.g. Erroneous Transfer
  service_action_name: string;
  // The action sub-type, e.g. Site Leaving
  service_action_type: string;
  // The action state, e.g. starting or finished
  service_action_state: string;
  // The action outcome, e.g. success or failure
  service_action_outcome: string;
  // The action origin, e.g. Energy, Fixed Line, Finance, etc
  service_action_origin: string;
  // The action app name e.g. Details, Smart UI, etc
  service_action_app_name: string;
  // The action service reference e.g.  E/G reference, Phone number, etc
  service_reference: string;
}

// The advisor clicks on a "service action"
// example usage: tracker.track(trackServiceActionClicked({prop: 'value'}, {option1: 'value'}));
export function serviceActionClickedEvent(
  context: ServiceActionClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "service_action_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/service_action_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackServiceActionClicked() {
  const { track } = useTracking();

  return (context: ServiceActionClickedEventContext, options?: Options) =>
    track(serviceActionClickedEvent(context, options));
}

export function useTrackServiceActionClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: ServiceActionClickedEventContext, options?: Options) =>
    trackOnce(serviceActionClickedEvent(context, options));
}

export interface AccountCategoryTabOpenedEventContext {
  // The tab name
  tab_name: string;
}

// The advisor opens an account category tab
// example usage: tracker.track(trackAccountCategoryTabOpened({prop: 'value'}, {option1: 'value'}));
export function accountCategoryTabOpenedEvent(
  context: AccountCategoryTabOpenedEventContext,
  options?: Options,
): EventTuple {
  return [
    "account_category_tab_opened",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/account_category_tab_opened/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAccountCategoryTabOpened() {
  const { track } = useTracking();

  return (context: AccountCategoryTabOpenedEventContext, options?: Options) =>
    track(accountCategoryTabOpenedEvent(context, options));
}

export function useTrackAccountCategoryTabOpenedOnce() {
  const trackOnce = useTrackOnce();

  return (context: AccountCategoryTabOpenedEventContext, options?: Options) =>
    trackOnce(accountCategoryTabOpenedEvent(context, options));
}

// The advisor views a list of account search results
// example usage: tracker.track(trackAccountSearchResultsDisplayed({prop: 'value'}, {option1: 'value'}));
export function accountSearchResultsDisplayedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "account_search_results_displayed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/account_search_results_displayed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAccountSearchResultsDisplayed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(accountSearchResultsDisplayedEvent(context, options));
}

export function useTrackAccountSearchResultsDisplayedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(accountSearchResultsDisplayedEvent(context, options));
}

export interface AddServiceClickedEventContext {
  // The type of service added
  service_type: string;
}

// The advisor clicks on "add service action"
// example usage: tracker.track(trackAddServiceClicked({prop: 'value'}, {option1: 'value'}));
export function addServiceClickedEvent(
  context: AddServiceClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "add_service_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/add_service_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAddServiceClicked() {
  const { track } = useTracking();

  return (context: AddServiceClickedEventContext, options?: Options) =>
    track(addServiceClickedEvent(context, options));
}

export function useTrackAddServiceClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: AddServiceClickedEventContext, options?: Options) =>
    trackOnce(addServiceClickedEvent(context, options));
}

export interface AddServiceActionClosedEventContext {
  // The type of service closed
  service_type: string;
}

// The advisor closes a "customer account" tab
// example usage: tracker.track(trackAddServiceActionClosed({prop: 'value'}, {option1: 'value'}));
export function addServiceActionClosedEvent(
  context: AddServiceActionClosedEventContext,
  options?: Options,
): EventTuple {
  return [
    "add_service_action_closed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/add_service_action_closed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAddServiceActionClosed() {
  const { track } = useTracking();

  return (context: AddServiceActionClosedEventContext, options?: Options) =>
    track(addServiceActionClosedEvent(context, options));
}

export function useTrackAddServiceActionClosedOnce() {
  const trackOnce = useTrackOnce();

  return (context: AddServiceActionClosedEventContext, options?: Options) =>
    trackOnce(addServiceActionClosedEvent(context, options));
}

// The advisor clicks on "copy link"
// example usage: tracker.track(trackCustomerVideoSupportCopyLinkClicked({prop: 'value'}, {option1: 'value'}));
export function customerVideoSupportCopyLinkClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "customer_video_support_copy_link_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/customer_video_support_copy_link_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCustomerVideoSupportCopyLinkClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(customerVideoSupportCopyLinkClickedEvent(context, options));
}

export function useTrackCustomerVideoSupportCopyLinkClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(customerVideoSupportCopyLinkClickedEvent(context, options));
}

// The advisor clicks on "create room"
// example usage: tracker.track(trackCustomerVideoSupportCreateRoomClicked({prop: 'value'}, {option1: 'value'}));
export function customerVideoSupportCreateRoomClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "customer_video_support_create_room_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/customer_video_support_create_room_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCustomerVideoSupportCreateRoomClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(customerVideoSupportCreateRoomClickedEvent(context, options));
}

export function useTrackCustomerVideoSupportCreateRoomClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(customerVideoSupportCreateRoomClickedEvent(context, options));
}

export interface EmailCannedMessageAddedEventContext {
  // The category/title of email
  canned_response_category: string;
  // The name of canned message
  canned_response_name: string;
}

// The advisor adds an email canned message
// example usage: tracker.track(trackEmailCannedMessageAdded({prop: 'value'}, {option1: 'value'}));
export function emailCannedMessageAddedEvent(
  context: EmailCannedMessageAddedEventContext,
  options?: Options,
): EventTuple {
  return [
    "email_canned_message_added",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/email_canned_message_added/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackEmailCannedMessageAdded() {
  const { track } = useTracking();

  return (context: EmailCannedMessageAddedEventContext, options?: Options) =>
    track(emailCannedMessageAddedEvent(context, options));
}

export function useTrackEmailCannedMessageAddedOnce() {
  const trackOnce = useTrackOnce();

  return (context: EmailCannedMessageAddedEventContext, options?: Options) =>
    trackOnce(emailCannedMessageAddedEvent(context, options));
}

export interface IdvIdentityQuestionAnsweredEventContext {
  // The type of "IDV identity answer" submitted, e.g. "ACCOUNT_HOLDER" | "ACCOUNT_NAME" | "AUTHORISED_3RD_PARTY" | "OTHER"
  identification_answer_type: string;
}

// The advisor submits an "IDV identity answer"
// example usage: tracker.track(trackIdvIdentityQuestionAnswered({prop: 'value'}, {option1: 'value'}));
export function idvIdentityQuestionAnsweredEvent(
  context: IdvIdentityQuestionAnsweredEventContext,
  options?: Options,
): EventTuple {
  return [
    "idv_identity_question_answered",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/idv_identity_question_answered/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackIdvIdentityQuestionAnswered() {
  const { track } = useTracking();

  return (
    context: IdvIdentityQuestionAnsweredEventContext,
    options?: Options,
  ) => track(idvIdentityQuestionAnsweredEvent(context, options));
}

export function useTrackIdvIdentityQuestionAnsweredOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: IdvIdentityQuestionAnsweredEventContext,
    options?: Options,
  ) => trackOnce(idvIdentityQuestionAnsweredEvent(context, options));
}

// The advisor finishes IDV
// example usage: tracker.track(trackIdvFinished({prop: 'value'}, {option1: 'value'}));
export function idvFinishedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "idv_finished",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/idv_finished/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackIdvFinished() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(idvFinishedEvent(context, options));
}

export function useTrackIdvFinishedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(idvFinishedEvent(context, options));
}

export interface IdvVerificationQuestionAnsweredEventContext {
  // The type of "IDV verification answer" submitted, e.g. "BANK_DETAILS" | "DOB" | "PASSWORD" | "RECOVERY"
  verification_answer_type: string;
}

// The advisor submits an "IDV verification answer"
// example usage: tracker.track(trackIdvVerificationQuestionAnswered({prop: 'value'}, {option1: 'value'}));
export function idvVerificationQuestionAnsweredEvent(
  context: IdvVerificationQuestionAnsweredEventContext,
  options?: Options,
): EventTuple {
  return [
    "idv_verification_question_answered",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/idv_verification_question_answered/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackIdvVerificationQuestionAnswered() {
  const { track } = useTracking();

  return (
    context: IdvVerificationQuestionAnsweredEventContext,
    options?: Options,
  ) => track(idvVerificationQuestionAnsweredEvent(context, options));
}

export function useTrackIdvVerificationQuestionAnsweredOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: IdvVerificationQuestionAnsweredEventContext,
    options?: Options,
  ) => trackOnce(idvVerificationQuestionAnsweredEvent(context, options));
}

export interface IdvStartedEventContext {
  // The account comment box is available to the advisor
  account_comment_box_available: boolean;
}

// The advisor starts IDV
// example usage: tracker.track(trackIdvStarted({prop: 'value'}, {option1: 'value'}));
export function idvStartedEvent(
  context: IdvStartedEventContext,
  options?: Options,
): EventTuple {
  return [
    "idv_started",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/idv_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackIdvStarted() {
  const { track } = useTracking();

  return (context: IdvStartedEventContext, options?: Options) =>
    track(idvStartedEvent(context, options));
}

export function useTrackIdvStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context: IdvStartedEventContext, options?: Options) =>
    trackOnce(idvStartedEvent(context, options));
}

export interface CallConsultEndedEventContext {
  // The name of the queue
  target_queue_name?: string;
}

// The advisor ends a consult
// example usage: tracker.track(trackCallConsultEnded({prop: 'value'}, {option1: 'value'}));
export function callConsultEndedEvent(
  context: CallConsultEndedEventContext,
  options?: Options,
): EventTuple {
  return [
    "call_consult_ended",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/call_consult_ended/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCallConsultEnded() {
  const { track } = useTracking();

  return (context: CallConsultEndedEventContext, options?: Options) =>
    track(callConsultEndedEvent(context, options));
}

export function useTrackCallConsultEndedOnce() {
  const trackOnce = useTrackOnce();

  return (context: CallConsultEndedEventContext, options?: Options) =>
    trackOnce(callConsultEndedEvent(context, options));
}

export interface IdvVerificationQuestionPromptedEventContext {
  // The type of "IDV verification question" prompted to the advisor, e.g. "BANK_DETAILS" | "DOB" | "PASSWORD" | "RECOVERY"
  verification_question_type: string;
}

// The advisor is prompted with an "IDV verification question"
// example usage: tracker.track(trackIdvVerificationQuestionPrompted({prop: 'value'}, {option1: 'value'}));
export function idvVerificationQuestionPromptedEvent(
  context: IdvVerificationQuestionPromptedEventContext,
  options?: Options,
): EventTuple {
  return [
    "idv_verification_question_prompted",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/idv_verification_question_prompted/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackIdvVerificationQuestionPrompted() {
  const { track } = useTracking();

  return (
    context: IdvVerificationQuestionPromptedEventContext,
    options?: Options,
  ) => track(idvVerificationQuestionPromptedEvent(context, options));
}

export function useTrackIdvVerificationQuestionPromptedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: IdvVerificationQuestionPromptedEventContext,
    options?: Options,
  ) => trackOnce(idvVerificationQuestionPromptedEvent(context, options));
}

// The advisor has lifted a call from hold
// example usage: tracker.track(trackCallUnheld({prop: 'value'}, {option1: 'value'}));
export function callUnheldEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "call_unheld",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/call_unheld/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCallUnheld() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(callUnheldEvent(context, options));
}

export function useTrackCallUnheldOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(callUnheldEvent(context, options));
}

// The advisor or client accepts a call
// example usage: tracker.track(trackCallAnswered({prop: 'value'}, {option1: 'value'}));
export function callAnsweredEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "call_answered",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/call_answered/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCallAnswered() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(callAnsweredEvent(context, options));
}

export function useTrackCallAnsweredOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(callAnsweredEvent(context, options));
}

// The advisor places a call on hold
// example usage: tracker.track(trackCallHeld({prop: 'value'}, {option1: 'value'}));
export function callHeldEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "call_held",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/call_held/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCallHeld() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(callHeldEvent(context, options));
}

export function useTrackCallHeldOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(callHeldEvent(context, options));
}

// The advisor begins notes (after call work)
// example usage: tracker.track(trackAcwStarted({prop: 'value'}, {option1: 'value'}));
export function acwStartedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "acw_started",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/acw_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAcwStarted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(acwStartedEvent(context, options));
}

export function useTrackAcwStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(acwStartedEvent(context, options));
}

// When a call is offered to an advisor
// example usage: tracker.track(trackInboundCallOffered({prop: 'value'}, {option1: 'value'}));
export function inboundCallOfferedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "inbound_call_offered",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/inbound_call_offered/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackInboundCallOffered() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(inboundCallOfferedEvent(context, options));
}

export function useTrackInboundCallOfferedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(inboundCallOfferedEvent(context, options));
}

export interface CallConsultStartedEventContext {
  // The name of the queue
  target_queue_name?: string;
}

// The advisor starts a consult
// example usage: tracker.track(trackCallConsultStarted({prop: 'value'}, {option1: 'value'}));
export function callConsultStartedEvent(
  context: CallConsultStartedEventContext,
  options?: Options,
): EventTuple {
  return [
    "call_consult_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/call_consult_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCallConsultStarted() {
  const { track } = useTracking();

  return (context: CallConsultStartedEventContext, options?: Options) =>
    track(callConsultStartedEvent(context, options));
}

export function useTrackCallConsultStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context: CallConsultStartedEventContext, options?: Options) =>
    trackOnce(callConsultStartedEvent(context, options));
}

export interface BillsViewerClickedEventContext {
  // The date of the customer bill
  date?: string;
  // The id of the customer bill
  id: string;
  // The reference of the customer bill
  reference?: string;
  // The total of the customer bill
  total?: string;
}

// The advisor clicks on a customer bill
// example usage: tracker.track(trackBillsViewerClicked({prop: 'value'}, {option1: 'value'}));
export function billsViewerClickedEvent(
  context: BillsViewerClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "bills_viewer_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/bills_viewer_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackBillsViewerClicked() {
  const { track } = useTracking();

  return (context: BillsViewerClickedEventContext, options?: Options) =>
    track(billsViewerClickedEvent(context, options));
}

export function useTrackBillsViewerClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: BillsViewerClickedEventContext, options?: Options) =>
    trackOnce(billsViewerClickedEvent(context, options));
}

// The advisor closed the "direct debit" journey
// example usage: tracker.track(trackDirectDebitJourneyClosed({prop: 'value'}, {option1: 'value'}));
export function directDebitJourneyClosedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "direct_debit_journey_closed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/direct_debit_journey_closed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackDirectDebitJourneyClosed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(directDebitJourneyClosedEvent(context, options));
}

export function useTrackDirectDebitJourneyClosedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(directDebitJourneyClosedEvent(context, options));
}

// The advisor successfully completes the "direct debit" journey
// example usage: tracker.track(trackDirectDebitJourneySucceeded({prop: 'value'}, {option1: 'value'}));
export function directDebitJourneySucceededEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "direct_debit_journey_succeeded",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/direct_debit_journey_succeeded/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackDirectDebitJourneySucceeded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(directDebitJourneySucceededEvent(context, options));
}

export function useTrackDirectDebitJourneySucceededOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(directDebitJourneySucceededEvent(context, options));
}

// The call ends
// example usage: tracker.track(trackCallEnded({prop: 'value'}, {option1: 'value'}));
export function callEndedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "call_ended",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/call_ended/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCallEnded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(callEndedEvent(context, options));
}

export function useTrackCallEndedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(callEndedEvent(context, options));
}

export interface PaymentJourneyClosedEventContext {
  // The origin of the payment journey, e.g. "MAKE_PAYMENT_JOURNEY" | "ADD_DIRECT_DEBIT_JOURNEY"
  origin: string;
}

// The advisor closes the payment journey
// example usage: tracker.track(trackPaymentJourneyClosed({prop: 'value'}, {option1: 'value'}));
export function paymentJourneyClosedEvent(
  context: PaymentJourneyClosedEventContext,
  options?: Options,
): EventTuple {
  return [
    "payment_journey_closed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_journey_closed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentJourneyClosed() {
  const { track } = useTracking();

  return (context: PaymentJourneyClosedEventContext, options?: Options) =>
    track(paymentJourneyClosedEvent(context, options));
}

export function useTrackPaymentJourneyClosedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PaymentJourneyClosedEventContext, options?: Options) =>
    trackOnce(paymentJourneyClosedEvent(context, options));
}

export interface PaymentJourneyFailedEventContext {
  // The payment amount option, e.g. "OVERDUE" | "DUE" | "TOTAL" | "OTHER"
  amount_option_selected: string;
  // The type of card, e.g. "NEW" | "EXISTING"
  card_type: string;
  // The card details are saved
  card_saved: boolean;
  // The error that caused the payment journey to fail
  error?: string;
}

// The advisor fails the payment journey
// example usage: tracker.track(trackPaymentJourneyFailed({prop: 'value'}, {option1: 'value'}));
export function paymentJourneyFailedEvent(
  context: PaymentJourneyFailedEventContext,
  options?: Options,
): EventTuple {
  return [
    "payment_journey_failed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_journey_failed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentJourneyFailed() {
  const { track } = useTracking();

  return (context: PaymentJourneyFailedEventContext, options?: Options) =>
    track(paymentJourneyFailedEvent(context, options));
}

export function useTrackPaymentJourneyFailedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PaymentJourneyFailedEventContext, options?: Options) =>
    trackOnce(paymentJourneyFailedEvent(context, options));
}

export interface PaymentJourneyStartedEventContext {
  // The origin of the payment journey
  origin: string;
}

// The advisor begins the payment journey
// example usage: tracker.track(trackPaymentJourneyStarted({prop: 'value'}, {option1: 'value'}));
export function paymentJourneyStartedEvent(
  context: PaymentJourneyStartedEventContext,
  options?: Options,
): EventTuple {
  return [
    "payment_journey_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_journey_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentJourneyStarted() {
  const { track } = useTracking();

  return (context: PaymentJourneyStartedEventContext, options?: Options) =>
    track(paymentJourneyStartedEvent(context, options));
}

export function useTrackPaymentJourneyStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PaymentJourneyStartedEventContext, options?: Options) =>
    trackOnce(paymentJourneyStartedEvent(context, options));
}

export interface EditBillTypeStartedEventContext {
  // The timestamp when the modal opens
  date_time?: string;
}

// The advisor clicks on "edit bill type" and opens the edit modal
// example usage: tracker.track(trackEditBillTypeStarted({prop: 'value'}, {option1: 'value'}));
export function editBillTypeStartedEvent(
  context: EditBillTypeStartedEventContext,
  options?: Options,
): EventTuple {
  return [
    "edit_bill_type_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/edit_bill_type_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackEditBillTypeStarted() {
  const { track } = useTracking();

  return (context: EditBillTypeStartedEventContext, options?: Options) =>
    track(editBillTypeStartedEvent(context, options));
}

export function useTrackEditBillTypeStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context: EditBillTypeStartedEventContext, options?: Options) =>
    trackOnce(editBillTypeStartedEvent(context, options));
}

// The advisor fails the "direct debit" journey
// example usage: tracker.track(trackDirectDebitJourneyFailed({prop: 'value'}, {option1: 'value'}));
export function directDebitJourneyFailedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "direct_debit_journey_failed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/direct_debit_journey_failed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackDirectDebitJourneyFailed() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(directDebitJourneyFailedEvent(context, options));
}

export function useTrackDirectDebitJourneyFailedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(directDebitJourneyFailedEvent(context, options));
}

// The advisor begins the "payment method selection" journey
// example usage: tracker.track(trackPaymentMethodStarted({prop: 'value'}, {option1: 'value'}));
export function paymentMethodStartedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "payment_method_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_method_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentMethodStarted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(paymentMethodStartedEvent(context, options));
}

export function useTrackPaymentMethodStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(paymentMethodStartedEvent(context, options));
}

// The advisor finishes taking notes (after call work)
// example usage: tracker.track(trackAcwEnded({prop: 'value'}, {option1: 'value'}));
export function acwEndedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "acw_ended",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/acw_ended/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAcwEnded() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(acwEndedEvent(context, options));
}

export function useTrackAcwEndedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(acwEndedEvent(context, options));
}

export interface PaymentUseNewCardStartedEventContext {
  // The amount of the payment journey
  amount: string;
  // The payment type of the payment journey
  payment_type: string;
  // The first name of the card holder
  card_holder_first_name: string;
  // The surname of the card holder
  card_holder_surname: string;
  // The first address field of the card holder
  address_primary: string;
  // The second address field of the card holder
  address_secondary: string;
  // The address postal code of the card holder
  address_postal_code: string;
}

// The advisor begins the payment journey with a new card
// example usage: tracker.track(trackPaymentUseNewCardStarted({prop: 'value'}, {option1: 'value'}));
export function paymentUseNewCardStartedEvent(
  context: PaymentUseNewCardStartedEventContext,
  options?: Options,
): EventTuple {
  return [
    "payment_use_new_card_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_use_new_card_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentUseNewCardStarted() {
  const { track } = useTracking();

  return (context: PaymentUseNewCardStartedEventContext, options?: Options) =>
    track(paymentUseNewCardStartedEvent(context, options));
}

export function useTrackPaymentUseNewCardStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PaymentUseNewCardStartedEventContext, options?: Options) =>
    trackOnce(paymentUseNewCardStartedEvent(context, options));
}

export interface PaymentUseNewCardFinishedEventContext {
  // The outcome of the payment journey, e.g. "SUCCESS" | "FAIL"
  outcome: string;
  // The card details are saved
  card_saved: boolean;
  // The error of the payment journey
  error?: string;
}

// The advisor completes the payment journey with a new card
// example usage: tracker.track(trackPaymentUseNewCardFinished({prop: 'value'}, {option1: 'value'}));
export function paymentUseNewCardFinishedEvent(
  context: PaymentUseNewCardFinishedEventContext,
  options?: Options,
): EventTuple {
  return [
    "payment_use_new_card_finished",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_use_new_card_finished/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentUseNewCardFinished() {
  const { track } = useTracking();

  return (context: PaymentUseNewCardFinishedEventContext, options?: Options) =>
    track(paymentUseNewCardFinishedEvent(context, options));
}

export function useTrackPaymentUseNewCardFinishedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PaymentUseNewCardFinishedEventContext, options?: Options) =>
    trackOnce(paymentUseNewCardFinishedEvent(context, options));
}

// The advisor confirms the payment journey
// example usage: tracker.track(trackPaymentConfirmStarted({prop: 'value'}, {option1: 'value'}));
export function paymentConfirmStartedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "payment_confirm_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_confirm_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentConfirmStarted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(paymentConfirmStartedEvent(context, options));
}

export function useTrackPaymentConfirmStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(paymentConfirmStartedEvent(context, options));
}

export interface PaymentJourneySucceededEventContext {
  // The payment amount option, e.g. "OVERDUE" | "DUE" | "TOTAL" | "OTHER"
  amount_option_selected: string;
  // The type of card, e.g. "NEW" | "EXISTING"
  card_type: string;
  // The card details are saved
  card_saved: boolean;
}

// The advisor successfully completes payment journey
// example usage: tracker.track(trackPaymentJourneySucceeded({prop: 'value'}, {option1: 'value'}));
export function paymentJourneySucceededEvent(
  context: PaymentJourneySucceededEventContext,
  options?: Options,
): EventTuple {
  return [
    "payment_journey_succeeded",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_journey_succeeded/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentJourneySucceeded() {
  const { track } = useTracking();

  return (context: PaymentJourneySucceededEventContext, options?: Options) =>
    track(paymentJourneySucceededEvent(context, options));
}

export function useTrackPaymentJourneySucceededOnce() {
  const trackOnce = useTrackOnce();

  return (context: PaymentJourneySucceededEventContext, options?: Options) =>
    trackOnce(paymentJourneySucceededEvent(context, options));
}

export interface ServiceIframeActionExecutedEventContext {
  // The action name, e.g. Erroneous Transfer
  service_action_name: string;
  // The action sub-type, e.g. Site Leaving
  service_action_type: string;
  // The action state, e.g. starting or finished
  service_action_state: string;
  // The action outcome, e.g. success or failure
  service_action_outcome: string;
  // The action origin, e.g. Energy, Fixed Line, Finance, etc
  service_action_origin: string;
  // The action app name e.g. Details, Smart UI, etc
  service_action_app_name: string;
  // The action service reference e.g.  E/G reference, Phone number, etc
  service_reference: string;
}

// The advisor executes a "service action" from within the iframe
// example usage: tracker.track(trackServiceIframeActionExecuted({prop: 'value'}, {option1: 'value'}));
export function serviceIframeActionExecutedEvent(
  context: ServiceIframeActionExecutedEventContext,
  options?: Options,
): EventTuple {
  return [
    "service_iframe_action_executed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/service_iframe_action_executed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackServiceIframeActionExecuted() {
  const { track } = useTracking();

  return (
    context: ServiceIframeActionExecutedEventContext,
    options?: Options,
  ) => track(serviceIframeActionExecutedEvent(context, options));
}

export function useTrackServiceIframeActionExecutedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: ServiceIframeActionExecutedEventContext,
    options?: Options,
  ) => trackOnce(serviceIframeActionExecutedEvent(context, options));
}

export interface CallFieldsEntity {
  // The conversation id associated with the call
  conversation_id: string;
  // The participant id associated with the call
  participant_id?: string;
  // The account number associated with the call
  account_number?: string;
  // The name of the queue of the call
  queue_name?: string;
  // The name of the skill of the call
  skill_name?: string;
  // The intent of the call
  intent?: string;
  // The account number associated to the tab that is opened
  opened_tab_account_number?: string;
}

// The details of the call
export function callFieldsEntity(
  context: CallFieldsEntity,
  options?: Options,
): EntityPayload<CallFieldsEntity> {
  return {
    ...options,
    name: "call_fields",
    data: context,
    snowplow: {
      schema: "iglu:uk.co.uw.customer-support/call_fields/jsonschema/1-0-1",
    },
  };
}

export interface KnowledgeArticlesSearchedEventContext {
  // The date and time of article search
  date_time: string;
  // The search term used for article search
  search_term: string;
  // The number of articles returned
  results: number;
  // The advisor's ID
  advisor_id: string;
}

// The advisor searches for a knowledge article
// example usage: tracker.track(trackKnowledgeArticlesSearched({prop: 'value'}, {option1: 'value'}));
export function knowledgeArticlesSearchedEvent(
  context: KnowledgeArticlesSearchedEventContext,
  options?: Options,
): EventTuple {
  return [
    "knowledge_articles_searched",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/knowledge_articles_searched/jsonschema/1-0-3",
      },
    },
  ];
}

export function useTrackKnowledgeArticlesSearched() {
  const { track } = useTracking();

  return (context: KnowledgeArticlesSearchedEventContext, options?: Options) =>
    track(knowledgeArticlesSearchedEvent(context, options));
}

export function useTrackKnowledgeArticlesSearchedOnce() {
  const trackOnce = useTrackOnce();

  return (context: KnowledgeArticlesSearchedEventContext, options?: Options) =>
    trackOnce(knowledgeArticlesSearchedEvent(context, options));
}

export interface SmsSentByAdvisorEventContext {
  // The phone number type (CALLER, ACCOUNT, MANUAL)
  phone_number_type?: string;
  // The message type (TEMPLATE, FREE)
  message_type?: string;
  // The length of the message
  message_length?: number;
  // The body of the message
  message_body?: string;
  // The category of the template
  template_category?: string;
  // The topic of the template
  template_topic?: string;
  // The outcome of the action (success or failure)
  outcome?: string;
}

// The advisor sends an SMS
// example usage: tracker.track(trackSmsSentByAdvisor({prop: 'value'}, {option1: 'value'}));
export function smsSentByAdvisorEvent(
  context: SmsSentByAdvisorEventContext,
  options?: Options,
): EventTuple {
  return [
    "sms_sent_by_advisor",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/sms_sent_by_advisor/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackSmsSentByAdvisor() {
  const { track } = useTracking();

  return (context: SmsSentByAdvisorEventContext, options?: Options) =>
    track(smsSentByAdvisorEvent(context, options));
}

export function useTrackSmsSentByAdvisorOnce() {
  const trackOnce = useTrackOnce();

  return (context: SmsSentByAdvisorEventContext, options?: Options) =>
    trackOnce(smsSentByAdvisorEvent(context, options));
}

export interface GenesysConnectionErrorEncounteredEventContext {
  // The reason for the connection error
  reason?: string;
  // The details of the connection error
  details?: string;
  // The conversation_id of the genesys client
  conversation_id?: string;
  // The participant_id of the genesys client
  participant_id?: string;
}

// The Genesys webrtc client encounters an error
// example usage: tracker.track(trackGenesysConnectionErrorEncountered({prop: 'value'}, {option1: 'value'}));
export function genesysConnectionErrorEncounteredEvent(
  context: GenesysConnectionErrorEncounteredEventContext,
  options?: Options,
): EventTuple {
  return [
    "genesys_connection_error_encountered",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/genesys_connection_error_encountered/jsonschema/1-0-0",
      },
    },
  ];
}

export function useTrackGenesysConnectionErrorEncountered() {
  const { track } = useTracking();

  return (
    context: GenesysConnectionErrorEncounteredEventContext,
    options?: Options,
  ) => track(genesysConnectionErrorEncounteredEvent(context, options));
}

export function useTrackGenesysConnectionErrorEncounteredOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: GenesysConnectionErrorEncounteredEventContext,
    options?: Options,
  ) => trackOnce(genesysConnectionErrorEncounteredEvent(context, options));
}

export interface IdvIdentityQuestionPromptedEventContext {
  // The type of "IDV identity question" prompted to the advisor, e.g. "ACCOUNT_HOLDER" | "ACCOUNT_NAME"
  identification_question_type: string;
}

// The advisor is prompted with an "IDV identity question"
// example usage: tracker.track(trackIdvIdentityQuestionPrompted({prop: 'value'}, {option1: 'value'}));
export function idvIdentityQuestionPromptedEvent(
  context: IdvIdentityQuestionPromptedEventContext,
  options?: Options,
): EventTuple {
  return [
    "idv_identity_question_prompted",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/idv_identity_question_prompted/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackIdvIdentityQuestionPrompted() {
  const { track } = useTracking();

  return (
    context: IdvIdentityQuestionPromptedEventContext,
    options?: Options,
  ) => track(idvIdentityQuestionPromptedEvent(context, options));
}

export function useTrackIdvIdentityQuestionPromptedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: IdvIdentityQuestionPromptedEventContext,
    options?: Options,
  ) => trackOnce(idvIdentityQuestionPromptedEvent(context, options));
}

export interface AccountHistoryActionsTakenFilterSubmittedEventContext {
  // The term used to filter actions
  term: string;
}

// The advisor submits a filtered search on actions taken
// example usage: tracker.track(trackAccountHistoryActionsTakenFilterSubmitted({prop: 'value'}, {option1: 'value'}));
export function accountHistoryActionsTakenFilterSubmittedEvent(
  context: AccountHistoryActionsTakenFilterSubmittedEventContext,
  options?: Options,
): EventTuple {
  return [
    "account_history_actions_taken_filter_submitted",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/account_history_actions_taken_filter_submitted/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAccountHistoryActionsTakenFilterSubmitted() {
  const { track } = useTracking();

  return (
    context: AccountHistoryActionsTakenFilterSubmittedEventContext,
    options?: Options,
  ) => track(accountHistoryActionsTakenFilterSubmittedEvent(context, options));
}

export function useTrackAccountHistoryActionsTakenFilterSubmittedOnce() {
  const trackOnce = useTrackOnce();

  return (
    context: AccountHistoryActionsTakenFilterSubmittedEventContext,
    options?: Options,
  ) =>
    trackOnce(accountHistoryActionsTakenFilterSubmittedEvent(context, options));
}

// The advisor clicks on "view room"
// example usage: tracker.track(trackCustomerVideoSupportViewRoomClicked({prop: 'value'}, {option1: 'value'}));
export function customerVideoSupportViewRoomClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "customer_video_support_view_room_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/customer_video_support_view_room_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCustomerVideoSupportViewRoomClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(customerVideoSupportViewRoomClickedEvent(context, options));
}

export function useTrackCustomerVideoSupportViewRoomClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(customerVideoSupportViewRoomClickedEvent(context, options));
}

export interface ServiceActionClosedEventContext {
  // The service action product
  product_type: string;
  // The service action name
  service_action_name: string;
}

// The advisor closes a product's "service action"
// example usage: tracker.track(trackServiceActionClosed({prop: 'value'}, {option1: 'value'}));
export function serviceActionClosedEvent(
  context: ServiceActionClosedEventContext,
  options?: Options,
): EventTuple {
  return [
    "service_action_closed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/service_action_closed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackServiceActionClosed() {
  const { track } = useTracking();

  return (context: ServiceActionClosedEventContext, options?: Options) =>
    track(serviceActionClosedEvent(context, options));
}

export function useTrackServiceActionClosedOnce() {
  const trackOnce = useTrackOnce();

  return (context: ServiceActionClosedEventContext, options?: Options) =>
    trackOnce(serviceActionClosedEvent(context, options));
}

export interface KnowledgeArticleClickedEventContext {
  // The origin type of the click e.g. "suggested" | "searched"
  origin: string;
  // The id of the article
  article_id: string;
  // The title of the article
  article_title: string;
}

// The advisor clicks on a knowledge article
// example usage: tracker.track(trackKnowledgeArticleClicked({prop: 'value'}, {option1: 'value'}));
export function knowledgeArticleClickedEvent(
  context: KnowledgeArticleClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "knowledge_article_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/knowledge_article_clicked/jsonschema/1-0-2",
      },
    },
  ];
}

export function useTrackKnowledgeArticleClicked() {
  const { track } = useTracking();

  return (context: KnowledgeArticleClickedEventContext, options?: Options) =>
    track(knowledgeArticleClickedEvent(context, options));
}

export function useTrackKnowledgeArticleClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: KnowledgeArticleClickedEventContext, options?: Options) =>
    trackOnce(knowledgeArticleClickedEvent(context, options));
}

// The advisor begins the "direct debit" journey
// example usage: tracker.track(trackDirectDebitJourneyStarted({prop: 'value'}, {option1: 'value'}));
export function directDebitJourneyStartedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "direct_debit_journey_started",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/direct_debit_journey_started/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackDirectDebitJourneyStarted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(directDebitJourneyStartedEvent(context, options));
}

export function useTrackDirectDebitJourneyStartedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(directDebitJourneyStartedEvent(context, options));
}

// The advisor hangs up a call
// example usage: tracker.track(trackCallHangUpClicked({prop: 'value'}, {option1: 'value'}));
export function callHangUpClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "call_hang_up_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/call_hang_up_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCallHangUpClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(callHangUpClickedEvent(context, options));
}

export function useTrackCallHangUpClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(callHangUpClickedEvent(context, options));
}

export interface EditBillTypeClosedEventContext {
  // The timestamp when the modal closed
  date_time?: string;
}

// The advisor closes the "edit bill type" modal
// example usage: tracker.track(trackEditBillTypeClosed({prop: 'value'}, {option1: 'value'}));
export function editBillTypeClosedEvent(
  context: EditBillTypeClosedEventContext,
  options?: Options,
): EventTuple {
  return [
    "edit_bill_type_closed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/edit_bill_type_closed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackEditBillTypeClosed() {
  const { track } = useTracking();

  return (context: EditBillTypeClosedEventContext, options?: Options) =>
    track(editBillTypeClosedEvent(context, options));
}

export function useTrackEditBillTypeClosedOnce() {
  const trackOnce = useTrackOnce();

  return (context: EditBillTypeClosedEventContext, options?: Options) =>
    trackOnce(editBillTypeClosedEvent(context, options));
}

// The advisor initiates an outbound call
// example usage: tracker.track(trackOutboundCallPlaced({prop: 'value'}, {option1: 'value'}));
export function outboundCallPlacedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "outbound_call_placed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/outbound_call_placed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackOutboundCallPlaced() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(outboundCallPlacedEvent(context, options));
}

export function useTrackOutboundCallPlacedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(outboundCallPlacedEvent(context, options));
}

// The advisor clicks on "extend notes" (after call work)
// example usage: tracker.track(trackAcwExtended({prop: 'value'}, {option1: 'value'}));
export function acwExtendedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "acw_extended",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/acw_extended/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAcwExtended() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(acwExtendedEvent(context, options));
}

export function useTrackAcwExtendedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(acwExtendedEvent(context, options));
}

export interface AccountTabOpenedEventContext {
  // The tab name
  tab_name: string;
  // The parent tab name
  parent_tab_name?: string;
}

// The advisor opens an account tab
// example usage: tracker.track(trackAccountTabOpened({prop: 'value'}, {option1: 'value'}));
export function accountTabOpenedEvent(
  context: AccountTabOpenedEventContext,
  options?: Options,
): EventTuple {
  return [
    "account_tab_opened",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/account_tab_opened/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAccountTabOpened() {
  const { track } = useTracking();

  return (context: AccountTabOpenedEventContext, options?: Options) =>
    track(accountTabOpenedEvent(context, options));
}

export function useTrackAccountTabOpenedOnce() {
  const trackOnce = useTrackOnce();

  return (context: AccountTabOpenedEventContext, options?: Options) =>
    trackOnce(accountTabOpenedEvent(context, options));
}

export interface PaymentMethodSelectedEventContext {
  // The type of card, e.g. "NEW" | "EXISTING"
  card_type: string;
  // The number of stored cards shown
  count_stored_cards_shown: number;
}

// The advisor selects a payment method
// example usage: tracker.track(trackPaymentMethodSelected({prop: 'value'}, {option1: 'value'}));
export function paymentMethodSelectedEvent(
  context: PaymentMethodSelectedEventContext,
  options?: Options,
): EventTuple {
  return [
    "payment_method_selected",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/payment_method_selected/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackPaymentMethodSelected() {
  const { track } = useTracking();

  return (context: PaymentMethodSelectedEventContext, options?: Options) =>
    track(paymentMethodSelectedEvent(context, options));
}

export function useTrackPaymentMethodSelectedOnce() {
  const trackOnce = useTrackOnce();

  return (context: PaymentMethodSelectedEventContext, options?: Options) =>
    trackOnce(paymentMethodSelectedEvent(context, options));
}

// The advisor clicks on an account search result
// example usage: tracker.track(trackAccountSearchResultsClicked({prop: 'value'}, {option1: 'value'}));
export function accountSearchResultsClickedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "account_search_results_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/account_search_results_clicked/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackAccountSearchResultsClicked() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(accountSearchResultsClickedEvent(context, options));
}

export function useTrackAccountSearchResultsClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(accountSearchResultsClickedEvent(context, options));
}

export interface WilliamFocusChangedEventContext {
  // Whether william is focused or not
  is_focused?: boolean;
}

// The william client gets focused or unfocused
// example usage: tracker.track(trackWilliamFocusChanged({prop: 'value'}, {option1: 'value'}));
export function williamFocusChangedEvent(
  context: WilliamFocusChangedEventContext,
  options?: Options,
): EventTuple {
  return [
    "william_focus_changed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/william_focus_changed/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackWilliamFocusChanged() {
  const { track } = useTracking();

  return (context: WilliamFocusChangedEventContext, options?: Options) =>
    track(williamFocusChangedEvent(context, options));
}

export function useTrackWilliamFocusChangedOnce() {
  const trackOnce = useTrackOnce();

  return (context: WilliamFocusChangedEventContext, options?: Options) =>
    trackOnce(williamFocusChangedEvent(context, options));
}

// The advisor opens the customer video support tab
// example usage: tracker.track(trackCustomerVideoSupportTabOpened({prop: 'value'}, {option1: 'value'}));
export function customerVideoSupportTabOpenedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "customer_video_support_tab_opened",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/customer_video_support_tab_opened/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCustomerVideoSupportTabOpened() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(customerVideoSupportTabOpenedEvent(context, options));
}

export function useTrackCustomerVideoSupportTabOpenedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(customerVideoSupportTabOpenedEvent(context, options));
}

export interface CallTransferredEventContext {
  // The name of the queue
  target_queue_name?: string;
}

// The advisor performs a call transfer
// example usage: tracker.track(trackCallTransferred({prop: 'value'}, {option1: 'value'}));
export function callTransferredEvent(
  context: CallTransferredEventContext,
  options?: Options,
): EventTuple {
  return [
    "call_transferred",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/call_transferred/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackCallTransferred() {
  const { track } = useTracking();

  return (context: CallTransferredEventContext, options?: Options) =>
    track(callTransferredEvent(context, options));
}

export function useTrackCallTransferredOnce() {
  const trackOnce = useTrackOnce();

  return (context: CallTransferredEventContext, options?: Options) =>
    trackOnce(callTransferredEvent(context, options));
}

// The advisor opens the SMS panel
// example usage: tracker.track(trackSmsPanelOpened({prop: 'value'}, {option1: 'value'}));
export function smsPanelOpenedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "sms_panel_opened",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/sms_panel_opened/jsonschema/1-0-1",
      },
    },
  ];
}

export function useTrackSmsPanelOpened() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(smsPanelOpenedEvent(context, options));
}

export function useTrackSmsPanelOpenedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(smsPanelOpenedEvent(context, options));
}

export interface KnowledgeFieldsEntity {
  // The id of the article
  article_id: string;
  // The title of the article
  article_title: string;
  // The revision of the article
  article_revision: string;
}

// The details of the knowledge article
export function knowledgeFieldsEntity(
  context: KnowledgeFieldsEntity,
  options?: Options,
): EntityPayload<KnowledgeFieldsEntity> {
  return {
    ...options,
    name: "knowledge_fields",
    data: context,
    snowplow: {
      schema:
        "iglu:uk.co.uw.customer-support/knowledge_fields/jsonschema/1-0-1",
    },
  };
}

// The advisor deletes a custom view
// example usage: tracker.track(trackCustomAdvisorViewDeleted({prop: 'value'}, {option1: 'value'}));
export function customAdvisorViewDeletedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "custom_advisor_view_deleted",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/custom_advisor_view_deleted/jsonschema/1-0-0",
      },
    },
  ];
}

export function useTrackCustomAdvisorViewDeleted() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(customAdvisorViewDeletedEvent(context, options));
}

export function useTrackCustomAdvisorViewDeletedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(customAdvisorViewDeletedEvent(context, options));
}

export interface TicketFieldsEntity {
  // The name of the custom view
  view_name?: string;
  // The sort path
  sort_path: string;
  // The sort order
  sort_order_by: string;
  // The view assignee filter
  filter_assignee?: string;
  // The view team filter
  filter_team?: string;
  // The view status filter
  filter_status?: string;
  // The view created by filter
  filter_created_by?: string;
  // The view template filter
  filter_type?: string;
}

// The fields related to ticketing views
export function ticketFieldsEntity(
  context: TicketFieldsEntity,
  options?: Options,
): EntityPayload<TicketFieldsEntity> {
  return {
    ...options,
    name: "ticket_fields",
    data: context,
    snowplow: {
      schema: "iglu:uk.co.uw.customer-support/ticket_fields/jsonschema/1-0-0",
    },
  };
}

// The advisor saves a custom view
// example usage: tracker.track(trackCustomAdvisorViewSaved({prop: 'value'}, {option1: 'value'}));
export function customAdvisorViewSavedEvent(
  context?: Context,
  options?: Options,
): EventTuple {
  return [
    "custom_advisor_view_saved",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/custom_advisor_view_saved/jsonschema/1-0-0",
      },
    },
  ];
}

export function useTrackCustomAdvisorViewSaved() {
  const { track } = useTracking();

  return (context?: Context, options?: Options) =>
    track(customAdvisorViewSavedEvent(context, options));
}

export function useTrackCustomAdvisorViewSavedOnce() {
  const trackOnce = useTrackOnce();

  return (context?: Context, options?: Options) =>
    trackOnce(customAdvisorViewSavedEvent(context, options));
}

export interface TicketViewSelectedEventContext {
  // If the view is saved or not
  view_is_saved?: boolean;
}

// The advisor selects the ticket view
// example usage: tracker.track(trackTicketViewSelected({prop: 'value'}, {option1: 'value'}));
export function ticketViewSelectedEvent(
  context: TicketViewSelectedEventContext,
  options?: Options,
): EventTuple {
  return [
    "ticket_view_selected",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/ticket_view_selected/jsonschema/1-0-2",
      },
    },
  ];
}

export function useTrackTicketViewSelected() {
  const { track } = useTracking();

  return (context: TicketViewSelectedEventContext, options?: Options) =>
    track(ticketViewSelectedEvent(context, options));
}

export function useTrackTicketViewSelectedOnce() {
  const trackOnce = useTrackOnce();

  return (context: TicketViewSelectedEventContext, options?: Options) =>
    trackOnce(ticketViewSelectedEvent(context, options));
}

export interface CreateNewTicketClickedEventContext {
  // The account number from where the create new ticket was clicked
  account_number?: string;
}

// The advisor clicks on "create new ticket"
// example usage: tracker.track(trackCreateNewTicketClicked({prop: 'value'}, {option1: 'value'}));
export function createNewTicketClickedEvent(
  context: CreateNewTicketClickedEventContext,
  options?: Options,
): EventTuple {
  return [
    "create_new_ticket_clicked",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/create_new_ticket_clicked/jsonschema/1-0-0",
      },
    },
  ];
}

export function useTrackCreateNewTicketClicked() {
  const { track } = useTracking();

  return (context: CreateNewTicketClickedEventContext, options?: Options) =>
    track(createNewTicketClickedEvent(context, options));
}

export function useTrackCreateNewTicketClickedOnce() {
  const trackOnce = useTrackOnce();

  return (context: CreateNewTicketClickedEventContext, options?: Options) =>
    trackOnce(createNewTicketClickedEvent(context, options));
}

export interface TicketFilterChangedEventContext {
  // ticket list tab or account ticket list
  context?: string;
  // The account number if context is account ticket list
  account_number?: string;
  // The name of the filter that has changed
  filter_name?: string;
  // The value of the filter that has changed
  filter_value?: string;
}

// The advisor changes a filter
// example usage: tracker.track(trackTicketFilterChanged({prop: 'value'}, {option1: 'value'}));
export function ticketFilterChangedEvent(
  context: TicketFilterChangedEventContext,
  options?: Options,
): EventTuple {
  return [
    "ticket_filter_changed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/ticket_filter_changed/jsonschema/1-0-0",
      },
    },
  ];
}

export function useTrackTicketFilterChanged() {
  const { track } = useTracking();

  return (context: TicketFilterChangedEventContext, options?: Options) =>
    track(ticketFilterChangedEvent(context, options));
}

export function useTrackTicketFilterChangedOnce() {
  const trackOnce = useTrackOnce();

  return (context: TicketFilterChangedEventContext, options?: Options) =>
    trackOnce(ticketFilterChangedEvent(context, options));
}

export interface TicketViewedEventContext {
  // The account number of the ticket viewed
  account_number?: string;
  // The id of the ticket viewed
  ticket_id?: string;
  // The type of the ticket viewed
  ticket_type?: string;
}

// The advisor views a ticket
// example usage: tracker.track(trackTicketViewed({prop: 'value'}, {option1: 'value'}));
export function ticketViewedEvent(
  context: TicketViewedEventContext,
  options?: Options,
): EventTuple {
  return [
    "ticket_viewed",
    context,
    {
      ...options,
      snowplow: {
        schema: "iglu:uk.co.uw.customer-support/ticket_viewed/jsonschema/1-0-0",
      },
    },
  ];
}

export function useTrackTicketViewed() {
  const { track } = useTracking();

  return (context: TicketViewedEventContext, options?: Options) =>
    track(ticketViewedEvent(context, options));
}

export function useTrackTicketViewedOnce() {
  const trackOnce = useTrackOnce();

  return (context: TicketViewedEventContext, options?: Options) =>
    trackOnce(ticketViewedEvent(context, options));
}

export interface TicketSortChangedEventContext {
  // ticket list tab or account ticket list
  context?: string;
  // The account number if context is account ticket list
  account_number?: string;
  // The name of the sort that has changed
  sort_name?: string;
  // The direction of the sort that has changed
  direction_of_sort?: string;
}

// The advisor changes the sort of a ticket
// example usage: tracker.track(trackTicketSortChanged({prop: 'value'}, {option1: 'value'}));
export function ticketSortChangedEvent(
  context: TicketSortChangedEventContext,
  options?: Options,
): EventTuple {
  return [
    "ticket_sort_changed",
    context,
    {
      ...options,
      snowplow: {
        schema:
          "iglu:uk.co.uw.customer-support/ticket_sort_changed/jsonschema/1-0-0",
      },
    },
  ];
}

export function useTrackTicketSortChanged() {
  const { track } = useTracking();

  return (context: TicketSortChangedEventContext, options?: Options) =>
    track(ticketSortChangedEvent(context, options));
}

export function useTrackTicketSortChangedOnce() {
  const trackOnce = useTrackOnce();

  return (context: TicketSortChangedEventContext, options?: Options) =>
    trackOnce(ticketSortChangedEvent(context, options));
}

export interface VulnerabilityFieldsEntity {
  // The vulnerability session id
  id: string;
  // The account ID for the advisor vulnerability journey
  account_id: string;
  // The account number for the self-serve vulnerability journey
  account_number?: string;
}

// The details of the Vulnerability journey
export function vulnerabilityFieldsEntity(
  context: VulnerabilityFieldsEntity,
  options?: Options,
): EntityPayload<VulnerabilityFieldsEntity> {
  return {
    ...options,
    name: "vulnerability_fields",
    data: context,
    snowplow: {
      schema:
        "iglu:uk.co.uw.customer-support/vulnerability_fields/jsonschema/1-0-3",
    },
  };
}
